// The oversize-zone guard.
//
// An ACCIDENT GUARD, not an agronomic limit. Davin once drew an enormous zone
// by mistake and afterwards every click on it froze the app for long enough
// that deleting it was genuinely hard. This stops that zone being created
// again. It does not make a large zone fast, and it is not trying to.
//
// Shefali's own view is that this is an edge case that will not really happen,
// which is why the answer is one number and two checks rather than the
// giant-zone performance work behind it (BBB-462).
//
// Its own module rather than three constants inside NewZoneScreen.js so the
// threshold and the arithmetic can be tested without standing up a 700-line
// screen and a Google Maps loader.

/**
 * Maximum area for a single zone, in hectares.
 *
 * 1,000 ha is roughly 10 square kilometres: far larger than any single field
 * and larger than most whole farms, so it catches an accidental drag across the
 * country and nothing a farmer would legitimately draw.
 *
 * CONFIRMED by Davin 11/09/2026 as an accident guard rather than an agronomic
 * limit, Shefali's view that this is an edge case already being on record.
 * Changing it is this one line, and both call sites and the message follow it.
 */
export const MAX_ZONE_AREA_HECTARES = 1000;

/**
 * What a farmer is told when their polygon is too big.
 *
 * Confirmed by Davin 11/09/2026. It has to say what to do instead, not only
 * that something was refused, which is why the second sentence exists. "Field"
 * here is the scale to draw at, deliberately kept where the soil legend moved
 * to "zone".
 */
export const OVERSIZE_ZONE_MESSAGE =
  `That area is too large to save as one zone (limit ${MAX_ZONE_AREA_HECTARES} ha). ` +
  'Draw a single field rather than the whole farm.';

/**
 * Area of a drawn path in hectares, to two decimal places, as a string.
 *
 * One helper rather than the same two lines in onPolygonComplete and
 * handlePolygonEdit, which is how the two could have drifted. Returns a string
 * because that is what `formData.surfaceArea` has always held and what the form
 * renders; use `isZoneOversize` rather than comparing it yourself.
 *
 * @param {google.maps.MVCArray} path the polygon's path
 * @returns {string} hectares to 2dp
 */
export function zoneAreaHectares(path) {
  const squareMetres = window.google.maps.geometry.spherical.computeArea(path);
  return (squareMetres / 10000).toFixed(2);
}

/**
 * Whether an area exceeds the cap.
 *
 * Takes the hectares value rather than a path, so it can gate a freshly drawn
 * polygon and a value already sitting in form state with the same comparison.
 * A non-numeric or absent value is NOT oversize: an empty form must fail on
 * "add a polygon", not on "too large".
 *
 * @param {string|number|null|undefined} hectares
 * @returns {boolean}
 */
export function isZoneOversize(hectares) {
  const value = Number(hectares);
  return Number.isFinite(value) && value > MAX_ZONE_AREA_HECTARES;
}
